import { useCallback } from "react"
import { Alert, Card, Col, Row } from "react-bootstrap"
import SaveDeleteButtons from "../../Components/default-components/buttons/SaveDeleteButtons"
import FormField from "../../Components/default-components/forms/FormField"
import GenericOverlay from "../../Components/default-components/overlays/GenericOverlay"
import PermissionContainer from "../../Hocs/withPermission"
import { useLocation } from "react-router-dom"
import { endPointCaderno } from "../../models/constantes"

/**
 * Componente de formulário de unidade com IoC (100% desacoplado)
 * Todas as dependências são injetadas via props
 */
function UnidadeForm({
	// Dados e callbacks
	initialData = {},
	onSave = null,
	onDelete = null,
	onCancel = null,

	// Dependências injetadas (obrigatórias)
	useUnidadeForm,
	useWebInterface,
	useToastService,
	wsProvider,

	// Configurações opcionais
	readOnly = false,
	showTitle = true,
}) {
	// ✅ SEMPRE chamar hooks primeiro (Rules of Hooks)
	// Usar valores seguros como fallback para evitar erros
	const mockWsProvider = {
		sendMessage: (msg) => console.log("[WS] Enviando mensagem:", msg),
		unsubscribe: (matchingObject) =>
			console.log("[WS] Unsubscribing:", matchingObject),
		subscribe: (matchingObject) =>
			console.log("[WS] Subscribing:", matchingObject),
		subscribeEvent: (event) =>
			console.log("[WS] Subscribing to event:", event),
		unsubscribeEvent: (event) =>
			console.log("[WS] Unsubscribing from event:", event),
	}

	const mockWebInterface = {
		get: async (url) => {
			console.log(`[HTTP GET] ${url}`)
			return { data: null }
		},
		post: async (url, body) => {
			console.log(`[HTTP POST] ${url}`, body)
			return { data: null }
		},
		put: async (url, body) => {
			console.log(`[HTTP PUT] ${url}`, body)
			return { data: null }
		},
	}

	// ✅ Mock toast se não foi injetado
	const mockToastService = () => ({
		success: (msg) => console.log(`✅ SUCCESS: ${msg}`),
		error: (msg) => console.log(`❌ ERROR: ${msg}`),
		warning: (msg) => console.log(`⚠️ WARNING: ${msg}`),
	})

	const location = useLocation()
	const initialFormData = location.state?.initialData || initialData || {}

	const { formData, errors, isLoading, isDirty, updateField, reset, submit, validate } =
		useUnidadeForm({
			initialData: initialFormData || {},
			useWebInterface: useWebInterface || mockWebInterface,
			wsProvider: wsProvider || mockWsProvider,
			useToastService: useToastService || mockToastService,
			endPointCaderno,
		})

	// ✅ useCallback ANTES das validações (Rules of Hooks)
	const handleSubmit = useCallback(async () => {
		const success = await submit(onSave)
		return success
	}, [submit, onSave])

	const handleCancel = useCallback(() => {
		if (isDirty) {
			const confirmCancel = window.confirm(
				"Você tem alterações não salvas. Deseja realmente cancelar?"
			)
			if (!confirmCancel) return
		}

		reset()
		if (onCancel) {
			onCancel()
		}
	}, [isDirty, reset, onCancel])

	return (
		<PermissionContainer
			component={() => (
				<Card className="mb-4">
					{showTitle && (
						<Card.Header className="d-flex justify-content-between align-items-center">
							<h5 className="mb-0">
								{initialData?.id
									? "Editar Unidade"
									: "Nova Unidade"}
							</h5>
						</Card.Header>
					)}

					<Card.Body>
						{Object.keys(errors).length > 0 && (
							<Alert variant="danger" className="mb-3">
								<strong>Preencha os seguintes campos obrigatórios:</strong>
								<ul className="mb-0 mt-2">
									{Object.entries(errors).map(
										([field, error]) => (
											<li key={field}>{error}</li>
										)
									)}
								</ul>
							</Alert>
						)}

						<Row>
							<Col md={6}>
								<FormField
									val={formData.nome}
									label="Unidade *"
									onValueUpdate={(value) =>
										updateField("nome", value)
									}
									isInvalid={!!errors.nome}
									feedback={errors.nome}
									disabled={readOnly || isLoading}
									maxLength={100}
									placeholder="Digite o nome da unidade"
									required
								/>
							</Col>

							<Col md={6}>
								<FormField
									val={formData.label}
									label="Sigla *"
									placeholder="Ex: L, ml, kg, g"
									onValueUpdate={(value) =>
										updateField("label", value)
									}
									isInvalid={!!errors.label}
									feedback={errors.label}
									disabled={readOnly || isLoading}
									maxLength={10}
									required
								/>
							</Col>
						</Row>

						<Row>
							<Col md={6}>
								<div className="d-flex">
									<div style={{ flex: 1 }}>
										<FormField
											val={formData.fatorSi}
											label="Fator de Conversão p/ SI *"
											ty="number"
											step="any"
											placeholder="Ex: 1 para L, 0.001 para ml"
											onValueUpdate={(value) =>
												updateField("fatorSi", value)
											}
											isInvalid={!!errors.fatorSi}
											feedback={errors.fatorSi}
											disabled={readOnly || isLoading}
											required
										/>
									</div>
									<div className="info-button">
										<GenericOverlay
											contentToDisplay={
												"Exemplos: 1 para L, 0.001 para ml, 1000 para kg"
											}
										/>
									</div>
								</div>
							</Col>

							<Col md={6}>
								<div className="d-flex">
									<div style={{ flex: 1 }}>
										<FormField
											val={formData.unidadeBaseSi}
											label="Unidade Base (SI) *"
											onValueUpdate={(value) =>
												updateField(
													"unidadeBaseSi",
													value
												)
											}
											isInvalid={!!errors.unidadeBaseSi}
											feedback={errors.unidadeBaseSi}
											disabled={readOnly || isLoading}
											placeholder="Ex: L, kg, m, s"
											required
										/>
									</div>
									<div className="info-button">
										<GenericOverlay
											contentToDisplay={
												"Unidades base do Sistema Internacional: L (litro), kg (quilograma), m (metro), s (segundo)"
											}
										/>
									</div>
								</div>
							</Col>
						</Row>

						{!readOnly && (
							<Row>
								<Col md={12}>
									<SaveDeleteButtons
										saveClick={handleSubmit}
										cancelEditClick={handleCancel}
										disabled={!isDirty}
										saveLabel={
											initialData?.id ? "Atualizar" : "Criar"
										}
										isEditing={!!initialData?.id}
									/>
								</Col>
							</Row>
						)}

						<style jsx>{`
							.info-button {
								display: flex;
								align-items: center;
								justify-content: center;
								padding: 8px;
								border-top: 1px solid #ddd;
								border-right: 1px solid #ddd;
								border-bottom: 1px solid #ddd;
								border-top-right-radius: 8px;
								border-bottom-right-radius: 8px;
								height: 58px;
								margin-top: 4px;
								margin-bottom: 4px;
							}
						`}</style>
					</Card.Body>
				</Card>
			)}
		/>
	)
}

export default UnidadeForm
